/**
 * AvailabilityModal (TECH-AVAIL-01) — add sick days, vacations, personal days,
 * meetings, errands, training for one tech (or several, for meetings) right from
 * the board, plus see + cancel the tech's upcoming events. The fluid same-day
 * path from research/tech-availability-design.md §2: no settings screen needed
 * when someone calls in sick at 7am.
 *
 * First real consumer of the permissions framework: saving is gated on
 * `team.add_timeoff` (admin wildcard / FAIL_OPEN keeps it usable today).
 * Honors the modal-close contract (outside-click + Esc + visible X).
 */
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarOff, Trash2, MapPin } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { searchPlaces, getPlace, type PlaceSuggestion } from "@/lib/places";
import type { ActiveTech } from "./api";
import {
  AVAIL_KIND_LABEL,
  useAddAvailability,
  useUpdateAvailability,
  useCancelAvailability,
  useCancelSeries,
  useUpcomingAvailability,
  type AvailKind,
  type AvailEvent,
  type NewAvailability,
} from "./availability";
import { Repeat } from "lucide-react";

function localDateOf(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function localTimeOf(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * UI kinds = the real kinds + "No more calls" (Seth's end-of-day marker: an Off
 * block from a start time through end of day, so the office knows not to add
 * more calls). Stored as kind='other' with the time block + title.
 */
type UiKind = AvailKind | "no_more_calls";

const KINDS: UiKind[] = [
  "sick",
  "vacation",
  "personal",
  "meeting",
  "errand",
  "part_pickup",
  "training",
  "no_more_calls",
  "other",
];

const UI_KIND_LABEL: Record<UiKind, string> = {
  sick: "Sick",
  vacation: "Vacation",
  personal: "Personal day",
  meeting: "Meeting",
  errand: "Errand",
  part_pickup: "Part pickup",
  training: "Training",
  no_more_calls: "No more calls",
  other: "Off",
};

/** Plain-language button labels (the action is ADD — deletes happen instantly
 * via the trash, no save step; "Save" here once read as "commit my deletion"
 * and minted a duplicate ticket). */
const ADD_LABEL: Record<UiKind, string> = {
  sick: "Add sick day",
  vacation: "Add vacation",
  personal: "Add personal day",
  meeting: "Add meeting",
  errand: "Add errand",
  part_pickup: "Add part pickup",
  training: "Add training",
  no_more_calls: "Add “no more calls”",
  other: "Add off ticket",
};

/** Kinds that default to a full day off vs a time block. */
const ALL_DAY_DEFAULT: Record<UiKind, boolean> = {
  sick: true,
  vacation: true,
  personal: true,
  other: true,
  meeting: false,
  errand: false,
  part_pickup: false,
  training: false,
  no_more_calls: false,
};

function fmtSpan(e: {
  start_at: string;
  end_at: string;
  all_day: boolean;
}): string {
  const s = new Date(e.start_at);
  const eEnd = new Date(e.end_at);
  const d = (x: Date) =>
    x.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const t = (x: Date) =>
    x.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (e.all_day) {
    const lastDay = new Date(eEnd);
    lastDay.setDate(lastDay.getDate() - 1); // exclusive end → last covered day
    return d(s) === d(lastDay) ? d(s) : `${d(s)} – ${d(lastDay)}`;
  }
  return `${d(s)} · ${t(s)} – ${t(eEnd)}`;
}

export function AvailabilityModal({
  defaultEmpno,
  date,
  techs,
  onClose,
  editEvent = null,
  onAdded,
}: {
  defaultEmpno: string;
  date: string; // YYYY-MM-DD (board date — the default day)
  techs: ActiveTech[];
  onClose: () => void;
  /** When set, the modal EDITS this existing entry (pre-filled, Save changes). */
  editEvent?: AvailEvent | null;
  /** Called after a successful ADD (so the board can react — e.g. offer to
   * rebuild the day when someone was just marked out with calls on the books). */
  onAdded?: (v: NewAvailability) => void;
}) {
  const canAdd = useCan("team.add_timeoff");
  const add = useAddAvailability();
  const update = useUpdateAvailability();
  const cancel = useCancelAvailability();
  const cancelSeries = useCancelSeries();
  const upcoming = useUpcomingAvailability(defaultEmpno).data ?? [];
  // RECUR-AVAIL-01 (Seth: "Andrew needs to be off by 2pm every Monday...
  // adding recurring events"): repeat weekly/biweekly through an until date.
  const [repeat, setRepeat] = useState<"none" | "weekly" | "biweekly">("none");
  const [repeatUntil, setRepeatUntil] = useState(() => {
    const d = new Date(`${date}T00:00:00`);
    d.setMonth(d.getMonth() + 3);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });

  const initKind: UiKind = editEvent
    ? editEvent.kind === "other" && editEvent.title === "No more calls"
      ? "no_more_calls"
      : editEvent.kind
    : "sick";
  const [kind, setKind] = useState<UiKind>(initKind);
  const [selected, setSelected] = useState<Set<string>>(
    new Set([editEvent?.empno ?? defaultEmpno]),
  );
  const [startDate, setStartDate] = useState(
    editEvent ? localDateOf(editEvent.start_at) : date,
  );
  const [endDate, setEndDate] = useState(
    editEvent
      ? editEvent.all_day
        ? localDateOf(
            new Date(new Date(editEvent.end_at).getTime() - 60000).toISOString(),
          )
        : localDateOf(editEvent.start_at)
      : date,
  );
  const [allDay, setAllDay] = useState(editEvent ? editEvent.all_day : true);
  const [startTime, setStartTime] = useState(
    editEvent && !editEvent.all_day ? localTimeOf(editEvent.start_at) : "09:00",
  );
  const [endTime, setEndTime] = useState(
    editEvent && !editEvent.all_day ? localTimeOf(editEvent.end_at) : "10:00",
  );
  const [title, setTitle] = useState(editEvent?.title ?? "");
  const [location, setLocation] = useState(editEvent?.location ?? "");
  // Coordinates ride along when the location was picked from Places — that's
  // what makes the event a map waypoint. Typing again clears them (free text).
  const [locationCoords, setLocationCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(
    editEvent?.location_lat != null && editEvent?.location_lng != null
      ? { lat: editEvent.location_lat, lng: editEvent.location_lng }
      : null,
  );
  const [placeSugs, setPlaceSugs] = useState<PlaceSuggestion[]>([]);
  const placeTimer = useRef<number | undefined>(undefined);
  // The suggestions render in a PORTAL pinned under the input (not inside the
  // modal body) — the body scrolls/clips, which cut the dropdown off (Seth).
  const locWrapRef = useRef<HTMLDivElement | null>(null);

  const onLocationInput = (text: string) => {
    setLocation(text);
    setLocationCoords(null); // free-typed text has no pin until re-picked
    if (placeTimer.current) window.clearTimeout(placeTimer.current);
    placeTimer.current = window.setTimeout(async () => {
      setPlaceSugs(await searchPlaces(text));
    }, 300);
  };

  const pickPlace = async (s: PlaceSuggestion) => {
    setPlaceSugs([]);
    const city = s.secondary.replace(/, USA$/, "");
    setLocation(city ? `${s.main}, ${city}` : s.main);
    const p = await getPlace(s.placeId);
    if (p) setLocationCoords({ lat: p.lat, lng: p.lng });
  };

  // Esc / outside-click / focus trap come from the shared Radix Modal.

  const pickKind = (k: UiKind) => {
    setKind(k);
    setAllDay(ALL_DAY_DEFAULT[k]);
    if (k === "no_more_calls") {
      // End-of-day marker: from a start time through the end of the day.
      setEndTime("23:59");
      if (!title.trim() || title === "No more calls") setTitle("No more calls");
    } else if (title === "No more calls") {
      setTitle("");
    }
  };

  const toggleTech = (empno: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(empno)) next.delete(empno);
      else next.add(empno);
      if (next.size === 0) next.add(empno); // never empty
      return next;
    });

  const techName = (t: ActiveTech) =>
    t.empname ?? (`${t.firstname ?? ""} ${t.lastname ?? ""}`.trim() || t.empno);

  const valid =
    selected.size > 0 &&
    startDate &&
    endDate &&
    endDate >= startDate &&
    (allDay || endTime > startTime) &&
    (repeat === "none" || repeatUntil >= startDate);

  const save = () => {
    const v: NewAvailability = {
      empnos: [...selected],
      kind: kind === "no_more_calls" ? "other" : kind,
      // A repeating block is one day per occurrence.
      startDate,
      endDate: allDay && repeat === "none" ? endDate : startDate,
      allDay,
      startTime,
      endTime,
      title,
      location,
      locationLat: locationCoords?.lat ?? null,
      locationLng: locationCoords?.lng ?? null,
      repeat,
      repeatUntil: repeat === "none" ? undefined : repeatUntil,
    };
    add.mutate(v, {
      onSuccess: () => {
        onClose();
        onAdded?.(v);
      },
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={editEvent ? "Edit blocked time" : "Block time"}
      icon={<CalendarOff className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" />}
      footer={
        <div className="flex items-center gap-2">
          {editEvent && (
            <button
              type="button"
              onClick={() =>
                cancel.mutate(editEvent.id, { onSuccess: onClose })
              }
              disabled={cancel.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {cancel.isPending ? "Removing…" : "Remove"}
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            Cancel
          </button>
          {editEvent ? (
            <button
              type="button"
              onClick={() =>
                update.mutate(
                  {
                    id: editEvent.id,
                    group_id: editEvent.group_id,
                    kind: kind === "no_more_calls" ? "other" : kind,
                    startDate,
                    endDate: allDay ? endDate : startDate,
                    allDay,
                    startTime,
                    endTime,
                    title,
                    location,
                    locationLat: locationCoords?.lat ?? null,
                    locationLng: locationCoords?.lng ?? null,
                  },
                  { onSuccess: onClose },
                )
              }
              disabled={!valid || !canAdd || update.isPending}
              className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
            >
              {update.isPending ? "Saving…" : "Save changes"}
            </button>
          ) : (
            <button
              type="button"
              onClick={save}
              disabled={!valid || !canAdd || add.isPending}
              title={!canAdd ? "Your role can't add time off" : undefined}
              className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
            >
              {add.isPending ? "Adding…" : repeat === "none" ? ADD_LABEL[kind] : `${ADD_LABEL[kind]} · repeats`}
            </button>
          )}
        </div>
      }
    >
        <div
          className="space-y-4"
          onScroll={() => placeSugs.length > 0 && setPlaceSugs([])}
        >
          {/* Kind */}
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Type
            </div>
            <div className="flex flex-wrap gap-1.5">
              {KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => pickKind(k)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                    kind === k
                      ? "border-brand-300 bg-brand-50 text-brand-800"
                      : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
                  )}
                >
                  {UI_KIND_LABEL[k]}
                </button>
              ))}
            </div>
          </div>

          {/* Techs (multi for meetings/training) — locked while editing */}
          {editEvent ? (
            <div className="text-xs text-slate-600">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                For{" "}
              </span>
              <span className="font-medium text-slate-800">
                {techName(
                  techs.find((t) => t.empno === editEvent.empno) ?? {
                    empno: editEvent.empno,
                    empname: editEvent.empno,
                    firstname: null,
                    lastname: null,
                    dept: null,
                  },
                )}
              </span>
              {editEvent.group_id && (
                <span className="ml-1 text-slate-400">
                  — changes apply to every attendee of this event
                </span>
              )}
            </div>
          ) : (
            <div>
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Tech{kind === "meeting" || kind === "training" ? "s" : ""}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {techs.map((t) => {
                  const on = selected.has(t.empno);
                  return (
                    <button
                      key={t.empno}
                      type="button"
                      onClick={() => toggleTech(t.empno)}
                      className={cn(
                        "rounded-full border px-2.5 py-1 text-xs transition-colors",
                        on
                          ? "border-slate-400 bg-slate-800 font-medium text-white"
                          : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
                      )}
                    >
                      {techName(t)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* When */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                From
              </span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (e.target.value > endDate) setEndDate(e.target.value);
                }}
                className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
              />
            </label>
            {allDay ? (
              <label className="block text-xs text-slate-600">
                <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Through
                </span>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
                />
              </label>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-xs text-slate-600">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Start
                  </span>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="block text-xs text-slate-600">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    End
                  </span>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
                  />
                </label>
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            All day{allDay && endDate !== startDate ? " (multi-day)" : ""}
          </label>

          {/* REPEATS — set it once ("off by 2pm every Monday"), Pegasus fills
              every week through the until date. ESC made you re-type it weekly. */}
          {!editEvent && (
            <div className="rounded-lg border border-hairline bg-slate-50/60 p-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                  <Repeat className="h-3.5 w-3.5 text-brand-500" /> Repeats
                </span>
                {([["none", "No"], ["weekly", "Every week"], ["biweekly", "Every 2 weeks"]] as const).map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setRepeat(k)}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                      repeat === k
                        ? "border-brand-300 bg-brand-50 text-brand-800"
                        : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
                    )}
                  >
                    {label}
                  </button>
                ))}
                {repeat !== "none" && (
                  <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-500">
                    until
                    <input
                      type="date"
                      value={repeatUntil}
                      min={startDate}
                      onChange={(e) => setRepeatUntil(e.target.value)}
                      className="rounded-md border border-hairline px-2 py-1 text-sm"
                    />
                  </label>
                )}
              </div>
              {repeat !== "none" && (
                <p className="mt-1.5 text-[11px] text-slate-400">
                  {allDay ? "One full day" : `${startTime}–${endTime}`} on that weekday, every {repeat === "weekly" ? "week" : "other week"} through the until date — added as individual entries you can still delete one at a time.
                </p>
              )}
            </div>
          )}

          {/* What / where */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Note
              </span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={
                  kind === "errand"
                    ? "Oil change @ Tires Plus"
                    : kind === "part_pickup"
                      ? "APR213 filter @ the supply house"
                      : kind === "meeting"
                        ? "Team meeting"
                        : "optional"
                }
                className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Location
              </span>
              <div ref={locWrapRef} className="relative">
                <input
                  type="text"
                  value={location}
                  onChange={(e) => onLocationInput(e.target.value)}
                  onBlur={() =>
                    window.setTimeout(() => setPlaceSugs([]), 150)
                  }
                  placeholder="business name or address (optional)"
                  className="w-full rounded-md border border-hairline px-2 py-1.5 pr-7 text-sm"
                />
                {locationCoords && (
                  <MapPin
                    className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-emerald-600"
                    aria-label="Pinned on the map"
                  />
                )}
              </div>
              {placeSugs.length > 0 &&
                locWrapRef.current &&
                createPortal(
                  (() => {
                    const rect = locWrapRef.current.getBoundingClientRect();
                    const width = Math.min(
                      Math.max(rect.width, 360),
                      window.innerWidth - 16,
                    );
                    const left = Math.min(
                      rect.left,
                      window.innerWidth - width - 8,
                    );
                    return (
                      <div
                        // data-stay-open: portal'd to body, so the Radix Modal
                        // must not treat clicks here as "outside" (see Modal.tsx).
                        data-stay-open
                        className="fixed z-[60] overflow-hidden rounded-lg border border-hairline bg-white shadow-e3"
                        style={{ left, top: rect.bottom + 4, width }}
                      >
                        {placeSugs.map((s) => (
                          <button
                            key={s.placeId}
                            type="button"
                            // onMouseDown beats the input's onBlur-clear
                            onMouseDown={(e) => {
                              e.preventDefault();
                              pickPlace(s);
                            }}
                            className="block w-full px-2.5 py-1.5 text-left transition-colors hover:bg-slate-50"
                          >
                            <span className="block truncate text-xs font-medium text-slate-800">
                              {s.main}
                            </span>
                            <span className="block truncate text-[11px] text-slate-500">
                              {s.secondary}
                            </span>
                          </button>
                        ))}
                      </div>
                    );
                  })(),
                  document.body,
                )}
            </label>
          </div>

          {/* Upcoming for this tech */}
          {!editEvent && upcoming.length > 0 && (
            <div>
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Scheduled for{" "}
                {techName(
                  techs.find((t) => t.empno === defaultEmpno) ?? {
                    empno: defaultEmpno,
                    empname: defaultEmpno,
                    firstname: null,
                    lastname: null,
                    dept: null,
                  },
                )}
              </div>
              <div className="space-y-1">
                {(() => {
                  // Repeating series collapse to ONE row (12 Mondays ≠ 12 rows).
                  const seen = new Set<string>();
                  const rows: { key: string; series: AvailEvent[] | null; e: AvailEvent }[] = [];
                  for (const e of upcoming) {
                    if (e.series_id) {
                      if (seen.has(e.series_id)) continue;
                      seen.add(e.series_id);
                      rows.push({ key: e.series_id, series: upcoming.filter((x) => x.series_id === e.series_id), e });
                    } else {
                      rows.push({ key: e.id, series: null, e });
                    }
                  }
                  const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
                  return rows.map(({ key, series, e }) => (
                    <div
                      key={key}
                      className="flex items-center gap-2 rounded-md border border-hairline bg-slate-50 px-2 py-1.5 text-xs"
                    >
                      {series && <Repeat className="h-3 w-3 shrink-0 text-brand-500" />}
                      <span className="font-medium text-slate-700">
                        {AVAIL_KIND_LABEL[e.kind]}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-slate-500">
                        {series
                          ? `Every ${DOW[new Date(e.start_at).getDay()]}${e.all_day ? "" : ` · ${new Date(e.start_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}–${new Date(e.end_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`} · ${series.length} left`
                          : fmtSpan(e)}
                        {e.title ? ` · ${e.title}` : ""}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          series
                            ? cancelSeries.mutate(e.series_id as string, { onSuccess: onClose })
                            : cancel.mutate(e.id, { onSuccess: onClose })
                        }
                        aria-label={series ? "Cancel the whole series" : "Cancel this entry"}
                        title={series ? "Remove every remaining occurrence of this series" : "Remove this entry (closes when done)"}
                        className="shrink-0 text-slate-300 transition-colors hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>
    </Modal>
  );
}
